import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Reveal from "@/components/Reveal";
import { FlaskConical, Droplets, Wind, Layers } from "lucide-react";

const actives = [
  {
    id: "antiseptic",
    label: "عامل مطهّر",
    detail: "يساعد على تنقية سطح الظفر والحدّ من انتشار الفطريات.",
  },
  {
    id: "salicylic",
    label: "حمض الساليسيليك",
    detail: "يقشّر بلطف الطبقات المتضررة ليفتح الطريق أمام تجدد الظفر.",
  },
  {
    id: "penetrating",
    label: "عوامل نافذة",
    detail: "توصل المكونات الفعالة إلى عمق الظفر حيث تختبئ الفطريات.",
  },
  {
    id: "repair",
    label: "عوامل مرمّمة",
    detail: "تدعم مظهر الأظافر المتضررة وتساند نموّاً صحياً جديداً.",
  },
];

export default function Bento() {
  const [active, setActive] = useState(actives[1]);

  return (
    <section id="benefits" className="relative mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:py-28">
      <Reveal>
        <p className="text-sm font-bold tracking-[0.3em] text-[#2E9E6B]">لماذا يوكيور؟</p>
        <h2 className="font-display mt-3 max-w-xl text-2xl font-black leading-snug text-[#0F1E19] sm:text-3xl lg:text-4xl">
          علم الصيدلة وطبيعة الأعشاب في زجاجة واحدة
        </h2>
      </Reveal>

      <div className="mt-12 grid gap-5 md:grid-cols-3">
        <Reveal className="md:col-span-2" delay={0.05}>
          <div
            className="grain relative h-full overflow-hidden rounded-3xl bg-[#0F1E19] p-8 text-[#FBF9F4] sm:p-10"
            data-testid="bento-formula-card"
          >
            <FlaskConical className="h-8 w-8 text-[#D4AF37]" />
            <h3 className="font-display mt-5 text-xl font-black sm:text-2xl">
              تركيبة هيدرو-كحولية مدروسة
            </h3>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-[#FBF9F4]/70 sm:text-base">
              غنية بمواد فعالة مضادة للفطريات معترف بها، تستهدف الأظافر المعرّضة للفطريات
              وتساعد على استعادة مظهرها الصحي دون عطور مضافة.
            </p>
            <div className="mt-8">
              <p className="mb-3 text-xs font-bold tracking-widest text-[#9BD8BC]">
                مختبر التركيبة — المس كل مكوّن فعّال
              </p>
              <div className="flex flex-wrap gap-2.5">
                {actives.map((a) => (
                  <button
                    key={a.id}
                    data-testid={`scanner-dot-${a.id}`}
                    onClick={() => setActive(a)}
                    className={`rounded-full border px-4 py-2 text-xs font-bold transition-all duration-300 ${
                      active.id === a.id
                        ? "border-[#D4AF37] bg-[#D4AF37] text-[#0F1E19]"
                        : "border-[#FBF9F4]/25 text-[#FBF9F4]/80 hover:border-[#D4AF37]/60 hover:text-[#FBF9F4]"
                    }`}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
              <AnimatePresence mode="wait">
                <motion.p
                  key={active.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.35 }}
                  className="mt-4 min-h-[3rem] text-sm leading-relaxed text-[#D4AF37]"
                  data-testid="scanner-detail"
                >
                  {active.detail}
                </motion.p>
              </AnimatePresence>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div
            className="flex h-full flex-col justify-between rounded-3xl border border-[#2E9E6B]/20 bg-[#E8F5EF] p-8"
            data-testid="bento-dry-card"
          >
            <Wind className="h-8 w-8 text-[#1F7A52]" />
            <div className="mt-8">
              <h3 className="font-display text-xl font-black text-[#0F1E19]">يجف في ثوانٍ</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#566B63]">
                رذاذ خفيف سريع الامتصاص — رشّ، انتظر لحظات، وتابع يومك دون لزوجة أو آثار.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <div
            className="flex h-full flex-col justify-between rounded-3xl border border-[#2E9E6B]/20 bg-white p-8"
            data-testid="bento-texture-card"
          >
            <Droplets className="h-8 w-8 text-[#2E9E6B]" />
            <div className="mt-8">
              <h3 className="font-display text-xl font-black text-[#0F1E19]">قوام خفيف</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#566B63]">
                تطبيق سهل ودقيق بفضل رأس الرش — مرة إلى مرتين يومياً على الظفر النظيف الجاف.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal className="md:col-span-2" delay={0.15}>
          <div
            className="relative h-full overflow-hidden rounded-3xl border border-[#D4AF37]/30 bg-[#F4F1EA] p-8 sm:p-10"
            data-testid="bento-penetration-card"
          >
            <div className="gold-line absolute inset-x-8 top-0 h-px" aria-hidden="true" />
            <Layers className="h-8 w-8 text-[#1F7A52]" />
            <h3 className="font-display mt-5 text-xl font-black text-[#0F1E19] sm:text-2xl">
              اختراق عميق… حيث تختبئ المشكلة
            </h3>
            <p className="mt-3 max-w-lg text-sm leading-relaxed text-[#566B63] sm:text-base">
              بفضل حمض الساليسيليك والعوامل النافذة، تعمل التركيبة في قلب الظفر لا على سطحه
              فقط، بينما تسهم العوامل الحامية والمرمّمة في تحسين مظهر الأظافر الهشّة ودعم
              نموّ أكثر صحة.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
